import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { TextInputform, TextArea, DropDown } from "../../components/Forms";
import { Buttons } from "../../components/Buttons";
import NotifyData from "../../components/NotifyData";
import {
  fetchComplaints,
  addComplaint,
  updateComplaint,
} from "../../slice/ComplaintSlice";

const ComplaintCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { complaints, customers, staff } = useSelector(
    (state) => state.complaints
  );
  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.user_id;
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const complaintSchema = [
    {
      name: "customer_id",
      textlabel: "Customer",
      type: "dropdown",
      required: true,
      disabled: isEditMode,
    },
    {
      name: "staff_id",
      textlabel: "Employee",
      type: "dropdown",
      required: true,
      disabled: isEditMode,
    },
    {
      name: "description",
      textlabel: "Description",
      type: "textarea",
      required: true,
    },
    {
      name: "status",
      textlabel: "Status",
      type: "dropdown",
      required: isEditMode,
      disabled: false,
    },
  ];

  const [formData, setFormData] = useState({
    customer_id: "",
    staff_id: "",
    description: "",
    status: isEditMode ? "" : "Pending",
  });

  useEffect(() => {
    dispatch(fetchComplaints(""));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && complaints.length > 0) {
      const complaint = complaints.find((c) => c.complaint_id === id);
      if (complaint) {
        setFormData({
          customer_id: complaint.customer_id || "",
          staff_id: complaint.staff_id || "",
          description: complaint.description || "",
          status: complaint.status || "",
        });
      }
    }
  }, [id, complaints, isEditMode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (
      !formData.customer_id ||
      !formData.staff_id ||
      !formData.description ||
      (isEditMode && !formData.status)
    ) {
      NotifyData(
        `${
          isEditMode ? "Complaint Update" : "Complaint Creation"
        } Failed: All fields are required`,
        "error"
      );
      setIsSubmitting(false);
      return;
    }

    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      setIsSubmitting(false);
      return;
    }

    const complaintData = {
      customer_id: formData.customer_id,
      staff_id: formData.staff_id,
      description: formData.description,
      status: formData.status,
      current_user_id: currentUserId,
      ...(isEditMode && { complaint_id: id }),
    };

    try {
      const action = isEditMode
        ? updateComplaint(complaintData)
        : addComplaint(complaintData);
      const msg = await dispatch(action).unwrap();
      NotifyData(msg, "success");
      navigate("/complaint");
    } catch (error) {
      NotifyData(error.message, "error");
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/complaint");
  };

  return (
    <div id="main">
      <Container fluid className="p-3">
        <Row className="">
          {complaintSchema.map((field, index) => (
            <Col lg="3" md="6" xs="12" className="py-3" key={index}>
              {field.type === "dropdown" ? (
                <DropDown
                  textlabel={field.textlabel}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  disabled={field.disabled || false}
                  options={
                    field.name === "customer_id"
                      ? customers.map((customer) => ({
                          value: customer.customer_id,
                          label: `${customer.name} (${customer.customer_no})`,
                        }))
                      : field.name === "staff_id"
                      ? staff.length > 0
                        ? staff.map((employee) => ({
                            value: employee.staff_id,
                            label: employee.staff_name,
                          }))
                        : [{ value: "", label: "No staff available" }]
                      : field.name === "status"
                      ? [
                          { value: "Pending", label: "Pending" },
                          { value: "In Progress", label: "In Progress" },
                          { value: "Resolved", label: "Resolved" },
                        ]
                      : []
                  }
                />
              ) : field.type === "textarea" ? (
                <TextArea
                  textlabel={field.textlabel}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                />
              ) : (
                <TextInputform
                  formLabel={field.formLabel}
                  PlaceHolder={field.formLabel}
                  name={field.name}
                  value={formData[field.name] || ""}
                  formtype={field.type}
                  onChange={handleChange}
                  className={field.classname || ""}
                  readOnly={field.disabled || false}
                />
              )}
            </Col>
          ))}
        </Row>
        <div className="align-items-end justify-content-center mt-5 d-flex custom-create">
          <div className="p-4">
            <Buttons
              btnlabel={
                isSubmitting
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                  ? "Update"
                  : "Submit"
              }
              className="border-0 submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
          </div>
          <div className="p-4">
            <Buttons
              btnlabel={<>Cancel</>}
              onClick={handleCancel}
              className="border-0 can-btn"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ComplaintCreation;
